import { useMemo } from "react";
import { parseCmsId, useGetCmsIssuers } from "api/cms";

/**
 * One contactable party's identity: the id we select and send with, plus what
 * we show for it.
 *
 * This is the ONLY place in the contact flow that knows where identity comes
 * from. Today it is the CMS issuer list — the same route the issuers page uses
 * (`GET /api/v1/issuers`) — because the dedicated issuer-contact route does not
 * exist yet. When it lands, re-point `fetch` below at it and map its fields to
 * this shape; nothing downstream (relevance filtering, the select grid, the
 * form) needs to change.
 */
export interface DirectoryEntry {
  /** Portal-side issuer id, as a string so int64 CMS ids join safely. */
  externalId: string;
  name: string;
  logoUrl?: string;
}

const toEntry = (issuer: {
  externalId?: number | string | null;
  name?: string | null;
  logoImage?: string | null;
}): DirectoryEntry | null => {
  const externalId = parseCmsId(issuer.externalId);
  if (externalId === null || !issuer.name) return null;
  return {
    externalId: String(externalId),
    name: issuer.name,
    logoUrl: issuer.logoImage ?? undefined,
  };
};

export const useIssuerDirectory = () => {
  const { loading, error, data } = useGetCmsIssuers();

  const entries = useMemo<DirectoryEntry[]>(() => {
    if (!data) return [];
    // NOTE: CMS issuers carry a `hasContact` flag that nothing reads yet. Once
    // the real contact route exists it should decide membership here — being in
    // the directory is what makes an issuer contactable (see the inner join in
    // useRelevantIssuers). We don't filter on it now because its live values
    // are unverified and an over-eager filter would empty the grid.
    return data
      .map(toEntry)
      .filter((entry): entry is DirectoryEntry => entry !== null);
  }, [data]);

  return { data: entries, loading, error };
};
